import { useEffect, useRef, useState } from 'react';
import { connectCashModule } from '../utilities/cashModuleClient';
import ChoosePaymentMethodModal from './ChoosePaymentMethodModal';
import CashPaymentDialog from './CashPaymentDialog';

function CashPaymentFlow({ cashModuleUrl, cashModuleEnabled, totalAmountCents, onChoosePin, onCashChosen, onCancel, onActivity, onPaymentComplete }) {
    const [step, setStep] = useState('choose');
    const [cashState, setCashState] = useState('connecting');
    const [session, setSession] = useState({ totalAmountCashCents: totalAmountCents, amountPaidCents: 0, remainingCents: totalAmountCents, changeOwedCents: 0, status: 'active' });
    const clientRef = useRef(null);
    const mountedRef = useRef(true);
    const completeTimeoutRef = useRef(null);
    const notifyActivity = onActivity || (() => { });

    function returnToChoose() {
        setSession({ totalAmountCashCents: totalAmountCents, amountPaidCents: 0, remainingCents: totalAmountCents, changeOwedCents: 0, status: 'active' });
        setStep('choose');
    }

    useEffect(() => {
        mountedRef.current = true;

        if (!cashModuleUrl) {
            setCashState('Disabled');
            return undefined;
        }

        function handleDisconnect() {
            if (!mountedRef.current) return;
            setCashState('Disabled');
            // If a Cash session is active when the socket drops, surface it as an
            // error there too (harmless no-op if still on the 'choose' step, since
            // chooseCash() resets session state fresh before the dialog is shown).
            setSession(prev => ({ ...prev, status: 'error' }));
        }

        const client = connectCashModule(cashModuleUrl, {
            onOpen: () => client.getState(),
            onClose: handleDisconnect,
            onError: handleDisconnect,
            onStateChanged: (state) => {
                if (!mountedRef.current) return;
                setCashState(state);
            },
            onCoinAccepted: (msg) => {
                if (!mountedRef.current) return;
                notifyActivity();
                setSession(prev => ({ ...prev, amountPaidCents: msg.amountPaidCents, remainingCents: msg.remainingCents, changeOwedCents: msg.changeOwedCents }));
            },
            onCoinRejected: () => { /* totals unchanged; the hardware physically releases the coin */ },
            onSessionStarted: (msg) => {
                if (!mountedRef.current) return;
                setSession(prev => ({ ...prev, totalAmountCashCents: msg.totalAmountCents, remainingCents: msg.remainingCents }));
            },
            onSessionComplete: (msg) => {
                if (!mountedRef.current) return;
                // Show the success state (with the change amount) briefly, then
                // advance automatically — no confirm button, so there's nothing to
                // misclick once the money's already been accepted.
                let completed;
                setSession(prev => {
                    completed = { ...prev, changeOwedCents: msg.changeDispensedCents, status: 'complete' };
                    return completed;
                });
                completeTimeoutRef.current = setTimeout(() => {
                    if (mountedRef.current && onPaymentComplete) {
                        onPaymentComplete({
                            totalAmountCashCents: completed.totalAmountCashCents,
                            amountPaidCents: completed.amountPaidCents,
                            changeDispensedCents: completed.changeOwedCents,
                        });
                    }
                }, 2000);
            },
            onSessionCancelled: (msg) => {
                if (!mountedRef.current) return;
                if (msg.reason === 'device-error') {
                    setSession(prev => ({ ...prev, status: 'error' }));
                } else {
                    returnToChoose();
                }
            },
        });
        clientRef.current = client;

        return () => {
            mountedRef.current = false;
            if (completeTimeoutRef.current) clearTimeout(completeTimeoutRef.current);
            client.cancelSession();
            client.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cashModuleUrl]);

    function chooseCash() {
        setSession({ totalAmountCashCents: totalAmountCents, amountPaidCents: 0, remainingCents: totalAmountCents, changeOwedCents: 0, status: 'active' });
        setStep('cash');
        clientRef.current?.startSession(totalAmountCents);
        if (onCashChosen) onCashChosen();
    }

    function cancelCash() {
        // A session that's already 'error' (device error / dropped connection) or
        // 'complete' (the module already cleared it server-side once the total was
        // reached) has no live session left to send CancelSession to — go back to
        // choosing a payment method directly instead of waiting for a
        // SessionCancelled confirmation that will never arrive.
        if (session.status === 'error' || session.status === 'complete') {
            returnToChoose();
            return;
        }
        clientRef.current?.cancelSession();
    }

    if (step === 'choose') {
        return (
            <ChoosePaymentMethodModal
                cashState={cashState}
                cashModuleEnabled={cashModuleEnabled}
                onChoosePin={onChoosePin}
                onChooseCash={chooseCash}
                onCancel={onCancel}
            />
        );
    }

    return (
        <CashPaymentDialog
            totalAmountCents={totalAmountCents}
            totalAmountCashCents={session.totalAmountCashCents}
            amountPaidCents={session.amountPaidCents}
            remainingCents={session.remainingCents}
            changeOwedCents={session.changeOwedCents}
            status={session.status}
            onCancel={cancelCash}
        />
    );
}

export default CashPaymentFlow;
